// Iter-64 — frontend hooks that pair with the backend `/api/geo` endpoint
// and localStorage to power:
//   • Hero-copy state localization on / and /auto-insurance
//   • "Pick up where you left off" saved-quote retention
//
// Both hooks gracefully degrade — if the geo lookup fails or localStorage
// is disabled (private browsing), the UI just falls back to generic copy.

import { useEffect, useState } from "react";
import { findStateByCode } from "./states";

const GEO_CACHE_KEY = "carsavr:detected-state";
const GEO_CACHE_TS_KEY = "carsavr:detected-state-ts";
const GEO_TTL_MS = 24 * 60 * 60 * 1000; // 24h — IP geo rarely changes

const QUOTE_PROGRESS_KEY = "carsavr:quote-progress";

// Returns { state, code, loading, source }
//   state — full state object from states.js, or null
//   code  — uppercase 2-letter code, or null
function readGeoCache() {
  try {
    const cachedCode = localStorage.getItem(GEO_CACHE_KEY);
    const cachedTs = Number(localStorage.getItem(GEO_CACHE_TS_KEY) || 0);
    if (cachedCode && Date.now() - cachedTs < GEO_TTL_MS) return cachedCode;
  } catch {
    // localStorage disabled — fall through to network
  }
  return null;
}

export function useDetectedState() {
  // Iter-416 — cache hits resolve synchronously in the initializer so the
  // very first render already knows the state (no post-paint layout shift
  // and no loading window for returning visitors).
  const [state, setState] = useState(() => {
    const cachedCode = readGeoCache();
    if (cachedCode) {
      return { state: findStateByCode(cachedCode), code: cachedCode, loading: false, source: "cache" };
    }
    return { state: null, code: null, loading: true, source: null };
  });

  useEffect(() => {
    if (!state.loading) return undefined;
    let cancelled = false;

    // 2. Backend lookup (will hit ipapi.co)
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/geo`;
    fetch(url, { credentials: "omit" })
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        const code = data?.state_code;
        if (code) {
          try {
            localStorage.setItem(GEO_CACHE_KEY, code);
            localStorage.setItem(GEO_CACHE_TS_KEY, String(Date.now()));
          } catch {/* ignore */}
          setState({
            state: findStateByCode(code),
            code,
            loading: false,
            source: data?.source || "ipapi",
          });
        } else {
          setState({ state: null, code: null, loading: false, source: data?.source || "unknown" });
        }
      })
      .catch(() => {
        if (!cancelled) setState({ state: null, code: null, loading: false, source: "error" });
      });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return state;
}

// Saved quote progress
//
// Shape: { vertical, zip, started_at }
// `vertical` is one of: "insurance" | "auto-loan" | "loan-refinance" | "warranty"
//
// We save the moment the user opens a QuoteModal vertical AND types a ZIP.
// On return visit (any page), `useSavedQuote()` returns the saved state so
// the homepage / sticky CTA can show "Pick up your <Vertical> quote".

export function loadSavedQuote() {
  try {
    const raw = localStorage.getItem(QUOTE_PROGRESS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Expire after 14 days — stale quotes hurt rather than help
    if (Date.now() - (parsed.started_at || 0) > 14 * 24 * 60 * 60 * 1000) {
      localStorage.removeItem(QUOTE_PROGRESS_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function saveQuoteProgress(vertical, zip) {
  if (!vertical) return;
  try {
    localStorage.setItem(
      QUOTE_PROGRESS_KEY,
      JSON.stringify({
        vertical,
        zip: zip || null,
        started_at: Date.now(),
      })
    );
  } catch {/* ignore */}
}

export function clearSavedQuote() {
  try {
    localStorage.removeItem(QUOTE_PROGRESS_KEY);
  } catch {/* ignore */}
}

export function useSavedQuote() {
  const [saved, setSaved] = useState(null);
  useEffect(() => {
    setSaved(loadSavedQuote());
  }, []);
  return { saved, refresh: () => setSaved(loadSavedQuote()), clear: () => { clearSavedQuote(); setSaved(null); } };
}
